<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Teams" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :block="true"
            @click="openTeamAddModalHandler"
          />
        </template>
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-sm-5"
      >
        <v-col cols="12" sm="6" md="4" lg="3" xl="2">
          <TeamsDropDownList
            v-model="teamNameFilter"
            label="Team Name"
            :clearable="true"
          ></TeamsDropDownList>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2">
          <UsersDropDownList
            :userId="managerIdFilter"
            label="Team Leader"
            @userIdChange="handleManagerIdChange"
            :clearable="true"
            :securityRoles="[MANAGER]"
          ></UsersDropDownList>
        </v-col>
      </v-row>
      <v-row justify="end">
        <v-col cols="12" sm="6">
          <FilterButtons
            @search="handleSearchClick"
            @clear="handleClearClick"
          ></FilterButtons>
        </v-col>
      </v-row>
      <FilterButtonsSmall
        @search="handleSearchClick"
        @clear="handleClearClick"
      ></FilterButtonsSmall>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          light
          :headers="headers"
          :items="teamsList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          item-key="id"
          class="elevation-2"
        >
          <template v-slot:item.action="{ item }">
            <v-btn class="mx-1 my-1"
                   color="primary"
                   outlined
                   @click="showEditTeamModal(item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn class="mx-1 my-1"
                   color="red"
                   outlined
                   @click="showDeleteTeamConfirmationDialog(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <TeamAddModal
      ref="TeamAddModal"
      @saveTeam="getTeamWithFilters"
    ></TeamAddModal>
    <TeamEditModal
      ref="TeamEditModal"
      :teamId="teamIdToEdit"
      :teamName="teamNameToEdit"
      :managerId="managerIdToEdit"
      :commissionBaseAmount="commissionBaseAmountToEdit"
      :commissionPercentage="commissionPercentageToEdit"
      :teamMembers="teamMembersToEdit"
      @teamNameChange="teamNameChangeHandler"
      @managerIdChange="managerIdChangeHandler"
      @commissionBaseAmountChange="commissionBaseAmountChangeHandler"
      @commissionPercentageChange="commissionPercentageChangeHandler"
      @clearTeam="clearTeamHandler"
      @editTeam="editTeamHandler"
      @addTeamMember="addTeamMemberHandler"
      @memberIdChange="memberIdChangeHandler"
      @roleChange="roleChangeHandler"
      @removeMember="removeMemberHandler"
    ></TeamEditModal>
    <ConfirmationModal
      :showModal="showDeleteTeamConfirmationDialogProp"
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected team?'"
      :toolbarColor="'primary'"
      @cancel-click="closeDeleteTeamConfirmationDialogHandler"
      @ok-click="deleteTeamHandler"
    ></ConfirmationModal>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { BottomSpacer, ButtonCustomIcon, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { UsersDropDownList } from '@/components/users'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import TeamAddModal from '@/components/teams/TeamAddModal'
import TeamEditModal from '@/components/teams/TeamEditModal'
import { ConfirmationModal } from '@/components/dialogs'
import TeamsDropDownList from '@/components/teams/TeamsDropDownList'
import { MANAGER } from '@/lib/constants/securityRoles'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  components: {
    TeamAddModal,
    TeamEditModal,
    SimplePageHeader,
    BottomSpacer,
    ButtonCustomIcon,
    FilterButtons,
    FilterButtonsSmall,
    UsersDropDownList,
    TeamsDropDownList,
    ConfirmationModal
  },
  data () {
    return {
      managerIdFilter: null,
      teamNameFilter: null,
      headers: [
        { text: 'Team Name', value: 'teamName', width: '35%' },
        { text: 'Team Leader', value: 'managerName', width: '35%' },
        { text: 'Actions', value: 'action', align: 'center', width: '30%', sortable: false }
      ],
      teamIdToEdit: null,
      teamNameToEdit: null,
      commissionBaseAmountToEdit: null,
      commissionPercentageToEdit: null,
      managerIdToEdit: null,
      teamMembersToEdit: [],
      showDeleteTeamConfirmationDialogProp: false,
      teamIdToDelete: null,
      MANAGER,
      hasFetchedAllTeams: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['teamName'],
        sortDesc: [false]
      },
      teamsList: []
    }
  },
  computed: {
    ...mapFields('teams', ['totalRecords']),
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()

        if (!this.shouldFetchTeams(newOptions, previousOptions)) {
          return
        }

        this.fetchTeams()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('teams', ['getTeams', 'getTeamsWithFiltersPaginated', 'deleteTeam']),
    handleManagerIdChange (newValue) {
      this.managerIdFilter = newValue
    },
    handleSearchClick () {
      this.options = { ...this.options, page: 1 }
    },
    handleClearClick () {
      this.teamNameFilter = null
      this.managerIdFilter = null
      this.options = { ...this.options, page: 1 }
    },
    openTeamAddModalHandler () {
      this.$refs.TeamAddModal.showModal()
    },
    showEditTeamModal (item) {
      this.teamIdToEdit = item.id
      this.teamNameToEdit = item.teamName
      this.managerIdToEdit = item.managerId
      this.commissionBaseAmountToEdit = item.commissionBaseAmount
      this.commissionPercentageToEdit = item.commissionPercentage
      this.teamMembersToEdit = item.teamMembers
      this.$refs.TeamEditModal.showModal()
    },
    teamNameChangeHandler (newValue) {
      this.teamNameToEdit = newValue
    },
    managerIdChangeHandler (newValue) {
      this.managerIdToEdit = newValue
    },
    commissionBaseAmountChangeHandler (newValue) {
      this.commissionBaseAmountToEdit = newValue
    },
    commissionPercentageChangeHandler (newValue) {
      this.commissionPercentageToEdit = newValue
    },
    clearTeamHandler () {
      this.teamIdToEdit = null
      this.teamNameToEdit = null
      this.managerIdToEdit = null
      this.commissionBaseAmountToEdit = null
      this.commissionPercentageToEdit = null
      this.teamMembersToEdit = []
      this.getTeamWithFilters()
    },
    addTeamMemberHandler () {
      this.teamMembersToEdit.push({ memberId: null, role: null })
    },
    memberIdChangeHandler ({ id, memberId }) {
      this.teamMembersToEdit[id]['memberId'] = memberId
    },
    roleChangeHandler ({ id, role }) {
      this.teamMembersToEdit[id]['role'] = role
    },
    removeMemberHandler (id) {
      this.teamMembersToEdit.splice(id, 1)
    },
    editTeamHandler () {
      this.getTeamWithFilters()
    },
    showDeleteTeamConfirmationDialog (item) {
      this.teamIdToDelete = item.id
      this.showDeleteTeamConfirmationDialogProp = true
    },
    closeDeleteTeamConfirmationDialogHandler () {
      this.teamIdToDelete = null
      this.showDeleteTeamConfirmationDialogProp = false
    },
    async deleteTeamHandler () {
      await this.deleteTeam(this.teamIdToDelete)
      this.showDeleteTeamConfirmationDialogProp = false
      this.getTeamWithFilters()
    },
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { teamName, managerId } = this.$route.query

      this.teamNameFilter = typeof teamName === 'string' && teamName.length ? teamName : null

      const parsedManagerId = Number(managerId)
      this.managerIdFilter = Number.isInteger(parsedManagerId) && parsedManagerId > 0 ? parsedManagerId : null
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)

      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)
      setOrDeleteQueryParam(nextQuery, 'teamName', this.getTeamNameFilterValue())
      setOrDeleteQueryParam(nextQuery, 'managerId', this.managerIdFilter)

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'teamName', 'managerId'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    getTeamNameFilterValue () {
      if (this.teamNameFilter && typeof this.teamNameFilter === 'object') {
        return this.teamNameFilter.teamName || null
      }

      return typeof this.teamNameFilter === 'string' && this.teamNameFilter.length
        ? this.teamNameFilter
        : null
    },
    fetchTeams () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options
      const filters = {
        teamName: this.getTeamNameFilterValue(),
        managerId: this.managerIdFilter
      }

      if (itemsPerPage === -1) {
        return this.getTeams(filters).then(teams => {
          this.hasFetchedAllTeams = true
          this.teamsList = teams
          return teams
        })
      }

      this.hasFetchedAllTeams = false
      return this.getTeamsWithFiltersPaginated({
        ...filters,
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      }).then(teams => {
        this.teamsList = teams
        return teams
      })
    },
    shouldFetchTeams (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllTeams)
    },
    getTeamWithFilters () {
      this.syncOptionsToQuery()
      return this.fetchTeams()
    }
  },
  created () {
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
  }
}
</script>
